import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
const WEB3FORMS_ACCESS_KEY = "38b1d9ac-ba24-47d3-9d0c-8f6ca82a50b3";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";

const Contact = () => {
  const { company } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
      if (form.website) {
        return;
      }
      setSubmitting(true);
      try {
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_key: WEB3FORMS_ACCESS_KEY,
            subject: `New enquiry from fletcher-stone.co.uk - ${form.name}`,
            from_name: "Fletcher & Stone website",
            name: form.name,
            email: form.email,
            phone: form.phone,
            service: form.service,
            message: form.message,
          }),
        });
        const result = await res.json();
        if (!result.success) throw new Error(result.message || "Could not send");
        toast.success("Thank you - we've received your enquiry and will be in touch shortly.");
        setForm({ name: "", email: "", phone: "", service: "", message: "", website: "" });
      } catch (err) {
        toast.error("Could not send your enquiry. Please try again, or call us directly.");
      } finally {
        setSubmitting(false);
      }
  };

  const inputCls = "w-full bg-transparent border-b border-[#d9d2c6] py-3 font-body text-[15px] text-[#23211e] placeholder:text-[#a49e94] focus:outline-none focus:border-[#94664f] transition-colors";

  return (
    <main className="pt-[76px]">
      <Seo
        title="Contact Us"
        description="Get in touch with Fletcher & Stone to discuss your new build, extension, refurbishment or restoration project across London, The Home Counties and The Cotswolds. Call 0203 488 7379 or send an enquiry."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact Us"
        title="Let's discuss your project"
        subtitle="Enquire about our services or discuss your project requirements. We would be delighted to hear from you."
        image="/refurbishment-kitchen.jpg"
      />

      <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Info */}
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">Get in touch</p>
            <h2 className="section-title text-[32px] md:text-[42px] mb-10">We're here to help</h2>
            <div className="space-y-8">
              <div className="flex gap-5">
                <Phone size={22} strokeWidth={1.3} className="text-[#94664f] mt-1 shrink-0" />
                <div>
                  <h4 className="font-body uppercase tracking-[0.18em] text-[11px] text-[#8a857d] mb-2">Telephone</h4>
                  <a href={`tel:${company.phone.replace(/\s/g, "")}`} className="font-body text-[15px] text-[#3a3833] hover:text-[#94664f] transition-colors block">{company.phone}</a>
                </div>
              </div>
              <div className="flex gap-5">
                <Mail size={22} strokeWidth={1.3} className="text-[#94664f] mt-1 shrink-0" />
                <div>
                  <h4 className="font-body uppercase tracking-[0.18em] text-[11px] text-[#8a857d] mb-2">Email</h4>
                  <a href={`mailto:${company.email}`} className="font-body text-[15px] text-[#3a3833] hover:text-[#94664f] transition-colors">{company.email}</a>
                </div>
              </div>
              <div className="flex gap-5">
                <Clock size={22} strokeWidth={1.3} className="text-[#94664f] mt-1 shrink-0" />
                <div>
                  <h4 className="font-body uppercase tracking-[0.18em] text-[11px] text-[#8a857d] mb-2">Hours</h4>
                  <p className="font-body text-[15px] text-[#3a3833]">{company.hours}</p>
                </div>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-[#eae5db] font-body text-[13px] text-[#8a857d] leading-[1.9]">
              Company No. {company.companyNo}<br />VAT No. {company.vatNo}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="bg-[#f6f2ec] p-8 md:p-12">
              <h3 className="font-display text-[28px] text-[#23211e] mb-8">Send us an enquiry</h3>
              <form onSubmit={handleSubmit} className="space-y-7">
                <input type="text" name="website" value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <input name="name" data-testid="contact-name" value={form.name} onChange={handleChange} placeholder="Full name *" className={inputCls} />
                  <input name="email" data-testid="contact-email" type="email" value={form.email} onChange={handleChange} placeholder="Email address *" className={inputCls} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <input name="phone" data-testid="contact-phone" value={form.phone} onChange={handleChange} placeholder="Telephone" className={inputCls} />
                  <input name="service" data-testid="contact-service" value={form.service} onChange={handleChange} placeholder="Type of project" className={inputCls} />
                </div>
                <textarea name="message" data-testid="contact-message" value={form.message} onChange={handleChange} rows={5} placeholder="Tell us about your project *" className={`${inputCls} resize-none`} />
                <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="btn-primary !bg-[#94664f] hover:!bg-[#23211e] w-full justify-center disabled:opacity-60">
                  {submitting ? "Sending…" : (<>Submit enquiry <Send size={15} /></>)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
